/**
 * How to describe where an attendance correction came from.
 *
 * The distinction matters to whoever reviews these. An employee's own request
 * is a claim they made about their own day and stand behind. An attestation is
 * an assertion made *about* them by someone with the power to change their pay,
 * and on those records the author and the approver are the same person — so
 * "Approved" does not mean a second party checked it.
 *
 * Flattening both to "corrected" in the listing would hide exactly that.
 */

export const isAttested = (request) =>
  Boolean(request?.source) && request.source !== "SELF";

export const SOURCE_LABEL = {
  SELF: "Employee request",
  SUPERVISOR_ATTESTED: "Supervisor attested",
  DEVICE_FAILURE: "Device failure",
  BULK: "Bulk correction",
};

export const sourceLabel = (request) =>
  SOURCE_LABEL[request?.source] || SOURCE_LABEL.SELF;

// Records predating the field have no source and were all self-raised, so they
// read as employee requests — which is what they were.
//
// Deliberately not violet: that already means "missing punch" everywhere else in
// this app's attendance vocabulary, and reusing it here would collide with a
// meaning readers have learned. The labels differ in words anyway, so nothing
// rests on colour alone.
export const originTone = (request) => (isAttested(request) ? "info" : "muted");

const personName = (person) => {
  if (!person || typeof person !== "object") return null;
  const name = `${person.fname || ""} ${person.lname || ""}`.trim();
  return name || null;
};

export const raisedByName = (request) => personName(request?.raisedBy);
export const reviewerName = (request) => personName(request?.reviewerId);

/**
 * True when the record was approved by the same person who raised it. Always the
 * case for an attestation — routing it back to its own author would be theatre —
 * but the listing should say so rather than let "Approved" imply review.
 */
export const isSelfApproved = (request) => {
  const author = request?.raisedBy?._id || request?.raisedBy;
  const reviewer = request?.reviewerId?._id || request?.reviewerId;
  return Boolean(author && reviewer && String(author) === String(reviewer));
};

export const POLICY_BYPASS_LABEL = {
  window: "outside the correction window",
  maxPerMonth: "over the monthly limit",
};

export const bypassSummary = (request) => {
  const list = request?.bypassedPolicy || [];
  if (!list.length) return null;
  return list.map((k) => POLICY_BYPASS_LABEL[k] || k).join(" and ");
};
